// The WGSL twins of `../terrain.ts`: the analytic terrain pass and the
// terrain composite.
//
// It is a source-for-source twin of the GLSL half. The same pieces are
// assembled in the same order, the same functions carry the same names, and
// the two files are read side by side. What changes is only what the port
// asks for:
//
//  * the DECLARATIONS are gone. WGSL has no `uniform` statement: the whole
//    block is generated from the pass's `UniformSpec[]` by
//    `worldHeaderWgsl`, and every read is `U.uName`;
//  * the preprocessor is gone. The GLSL writes `const float NEAR_EPS =
//    float(<figure>)` and the builder substitutes the figure; the twin
//    writes `const NEAR_EPS: f32 = <figure>` and substitutes the same one;
//  * a plane is read with `textureSampleLevel(…, sLinear, …, 0.0)` and never
//    with `textureSample`, because the taps of the composite sit under an
//    `if` and a sampling call in non-uniform control flow fails the
//    uniformity analysis. The plane carries one mip level, so an explicit
//    level 0 is the same tap;
//  * `texelFetch` of the component plane is `textureLoad`;
//  * the ternary that reads the centre cell or a neighbour is an `if`,
//    because `select` evaluates BOTH arms and `neighbor(c0, -1)` would index
//    the hex table out of range;
//  * `any` is a WGSL builtin, so the flag of `anyTerrain` is `anyT`.
//
// The three LOOKS live in `../terrain-styles.ts` beside their GLSL, one
// table for both languages.
//
// **Every piece here is a function and nothing is a module const.** The GLSL
// half imports this file to fill the `wgsl` field of its two `…Program`
// builders, and this file imports the GLSL half for the figures and the
// uniform lists, so the two modules are a cycle. A function is hoisted and
// is callable before a module body has run; a `const` is not. So the pieces
// are functions, the shipping constants live beside their GLSL twins in
// `../terrain.ts`, and no import order can leave a name unset.

import { TERRAIN_NEAR_EPS, TERRAIN_STYLE_DEFAULT, terrainAllUniforms, terrainTexAllUniforms } from '../terrain';
import { terrainPaintSrcWgsl, terrainStyleHelpersWgsl } from '../terrain-styles';
import { PRELUDE_BODY_WGSL } from './prelude';
import { wgslModule } from './module';
import type { TerrainStyle } from '../../../ui/settings';

/** Everything every look shares once it has the two fields. */
function baseBodyWgsl(): string {
  return `
// The world point p as a uv of the terrain plane.
fn distUv(p: vec2f) -> vec2f { return (p - U.uDistLo) * U.uDistUv; }

// ---- the distance channels --------------------------------------------
//
// A cubic B-spline sampled with FOUR bilinear taps (Sigg and Hadwiger). The
// sixteen texels of the 4x4 support are read as four weighted bilinear
// fetches, because every weight of the B-spline basis is positive and each
// axis pair therefore has an interior sample point that carries both of its
// texels. The result is C2, so an iso-line of it is a smooth curve and not
// the polygon a bilinear tap draws (R432, the void core).
fn distSpline(uv: vec2f) -> vec2f {
  let texel = U.uDistUv * U.uDistStep;       // one texel in uv
  let size = 1.0 / texel;                    // the plane in texels
  let coord = uv * size - 0.5;
  let base = floor(coord);
  let f = coord - base;
  let f2 = f * f;
  let f3 = f2 * f;
  let w0 = (1.0 / 6.0) * (-f3 + 3.0 * f2 - 3.0 * f + 1.0);
  let w1 = (1.0 / 6.0) * (3.0 * f3 - 6.0 * f2 + 4.0);
  let w2 = (1.0 / 6.0) * (-3.0 * f3 + 3.0 * f2 + 3.0 * f + 1.0);
  let w3 = (1.0 / 6.0) * f3;
  let s0 = w0 + w1;                          // the four weights sum to 1
  let s1 = w2 + w3;
  let t0 = (base + 0.5 + (w1 / s0) - 1.0) * texel;
  let t1 = (base + 0.5 + (w3 / s1) + 1.0) * texel;
  let lo = mix(textureSampleLevel(uDist, sLinear, vec2f(t0.x, t0.y), 0.0).rg,
               textureSampleLevel(uDist, sLinear, vec2f(t1.x, t0.y), 0.0).rg, s1.x);
  let hi = mix(textureSampleLevel(uDist, sLinear, vec2f(t0.x, t1.y), 0.0).rg,
               textureSampleLevel(uDist, sLinear, vec2f(t1.x, t1.y), 0.0).rg, s1.x);
  return mix(lo, hi, s1.y);
}

// The two inside-distances at a world point, in world px.
//
// The knob is an \`if\` and not a \`select\`: a select evaluates both arms, so
// it would pay the four taps of the spline on every pixel that asked for
// the one bilinear tap.
fn distAt(uv: vec2f) -> vec2f {
  if (U.uDistTap == 1) { return distSpline(uv); }
  return textureSampleLevel(uDist, sLinear, uv, 0.0).rg;
}

// ---- the hash every scatter of this layer agrees on --------------------
//
// A WGSL parameter is immutable, so the hash turns its argument over in a
// var of its own. The shift counts are u32 and the constants keep their u
// suffix, which is what keeps the arithmetic the same on both backends.
fn hashU(xIn: u32) -> u32 {
  var x = xIn;
  x ^= x >> 16u; x *= 0x7feb352du;
  x ^= x >> 15u; x *= 0x846ca68bu;
  x ^= x >> 16u;
  return x;
}
fn hash01(h: u32) -> f32 { return f32(h & 0x00ffffffu) / 16777216.0; }
`;
}

/** The early-out every look shares, after the picture. */
function anyTerrainWgsl(): string {
  return `
// Is there any terrain in reach of this pixel?
//
// The two fields read seven cells and no more, and the hachures of an open
// pixel hang off those same six neighbours. When every one of the seven is
// open sea the pass draws nothing at all, so it answers with seven texel
// fetches instead of seven kernels and six edges. Most of the map is open
// sea (R431, the GPU work).
//
// The flag is \`anyT\`, because \`any\` is a WGSL builtin.
fn anyTerrain(c0: vec2i, kind0: i32) -> bool {
  var anyT = kind0 != 0;
  for (var k: i32 = 0; k < 6; k++) {
    if (kindOf(neighbor(c0, k)) != 0) { anyT = true; }
  }
  return anyT;
}
`;
}

/** Everything both programs of ONE look share, after the prelude body. */
function commonSrcWgsl(style: TerrainStyle): string {
  return baseBodyWgsl() + terrainStyleHelpersWgsl(style)
    + terrainPaintSrcWgsl(style) + anyTerrainWgsl();
}

// ---------------------------------------------------------------------------
// The analytic pass (the fallback, and `#terrain=analytic`)
// ---------------------------------------------------------------------------

function analyticWgsl(): string {
  return `
@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  let p = v.vWorld;
  let px = worldPx();
  let c0 = cellAt(p);
  let kind0 = kindOf(c0);
  if (!anyTerrain(c0, kind0)) { return vec4f(0.0); }

  // ---- the two ring-1 fields -------------------------------------------
  let invRT2 = 1.0 / (U.uRadiusT * U.uRadiusT);
  var W = 0.0;
  var G = vec2f(0.0);
  var wT = 0.0;
  var gT = vec2f(0.0);
  var wR = 0.0;
  var gR = vec2f(0.0);
  for (var i: i32 = 0; i < 7; i++) {
    // The centre cell first, then its six neighbours. GLSL writes it as a
    // ternary; WGSL must not, because a select evaluates both arms and
    // neighbor(c0, -1) would index the hex table out of range.
    var c = c0;
    if (i > 0) { c = neighbor(c0, i - 1); }
    var k = 0.0;
    var dk = vec2f(0.0);
    kern(p, cellCenter(c), invRT2, &k, &dk);
    W += k;
    G += dk;
    let kind = kindOf(c);
    if (kind == 1) { wT += k; gT += dk; }
    else if (kind == 2) { wR += k; gR += dk; }
  }
  if (W < 1e-6) { return vec4f(0.0); }

  let fT = wT / W;
  let dfT = (gT - fT * G) / W;
  let fR = wR / W;
  let dfR = (gR - fR * G) / W;
  // Each field rises toward its own body, so its gradient points INWARD.
  // The unit form of it is the normal a look copies a body along, and it
  // is taken here because the same division already makes the distance.
  let lT = max(length(dfT), 1e-5);
  let lR = max(length(dfR), 1e-5);
  let sT = (fT - 0.5) / lT;
  let sR = (fR - 0.5) / lR;

  return terrainPaint(p, c0, kind0, px, sT, sR, dfT / lT, dfR / lR);
}
`;
}

// ---------------------------------------------------------------------------
// The composite pass (the default)
// ---------------------------------------------------------------------------

function texturedWgsl(): string {
  return `
// What the plane calls "no terrain in reach". See TERRAIN_NEAR_EPS. WGSL has
// no preprocessor either, so the builder substitutes the figure the GLSL
// builder substitutes.
const NEAR_EPS: f32 = ${TERRAIN_NEAR_EPS};

// The whole pass as ONE function. The cell and the pixel size come in;
// \`fs\` below reads them exactly where this pass always read them.
fn terrainMain(p: vec2f, px: f32, c0: vec2i, kind0: i32) -> vec4f {
  // ---- is any terrain in reach? ----------------------------------------
  //
  // Two ways to answer, and the knob picks one (early=plane is the default
  // since R493, the frame-throughput report of 2026-09-04, package E).
  //
  //  * fetch runs the scan: seven texel fetches of the blocked plane.
  //  * plane reads the ONE tap the body needs anyway. Both fields are
  //    exactly zero where the kernel reaches no terrain, so a tap under
  //    NEAR_EPS means the pass would draw nothing: every stroke this pass
  //    paints is gated by the coverage of fT or fR, and both are far below
  //    their 0.5 crossing there.
  //
  // The tap is taken ONCE. Reading it twice would pay the very fetch the
  // knob exists to remove.
  let uv = distUv(p);
  var c = vec4f(0.0);
  if (U.uEarlyPlane == 1) {
    c = textureSampleLevel(uDist, sLinear, uv, 0.0);
    if (max(c.b, c.a) <= NEAR_EPS) { return vec4f(0.0); }
  } else {
    if (!anyTerrain(c0, kind0)) { return vec4f(0.0); }
    c = textureSampleLevel(uDist, sLinear, uv, 0.0);
  }

  // ---- the two fields, read rather than summed --------------------------
  //
  // B is fT and A is fR. The gradient is a central difference over ONE
  // texel of the plane, which is the same construction the territory
  // composite uses; the two fields are one function of position on both
  // sides of their rim, so the difference is continuous across it and the
  // prelude's rule against a screen-space derivative still holds.
  //
  // The tap stays BILINEAR here. The rim is a single iso-line of a field
  // that the raster low-passed over 1.8 hex radii, so a piecewise-linear
  // read of it wobbles by about a twentieth of a world px, and the raster's
  // own 0.5 crossing is what the EDT was aligned to (R432).
  let texel = U.uDistUv * U.uDistStep;
  let tx = vec2f(texel.x, 0.0);
  let ty = vec2f(0.0, texel.y);
  let xp = textureSampleLevel(uDist, sLinear, uv + tx, 0.0);
  let xm = textureSampleLevel(uDist, sLinear, uv - tx, 0.0);
  let yp = textureSampleLevel(uDist, sLinear, uv + ty, 0.0);
  let ym = textureSampleLevel(uDist, sLinear, uv - ty, 0.0);

  let dfT = vec2f((xp.b - xm.b) / (2.0 * U.uDistStep.x),
                  (yp.b - ym.b) / (2.0 * U.uDistStep.y));
  let dfR = vec2f((xp.a - xm.a) / (2.0 * U.uDistStep.x),
                  (yp.a - ym.a) / (2.0 * U.uDistStep.y));
  let lT = max(length(dfT), 1e-5);
  let lR = max(length(dfR), 1e-5);
  let sT = (c.b - 0.5) / lT;
  let sR = (c.a - 0.5) / lR;

  return terrainPaint(p, c0, kind0, px, sT, sR, dfT / lT, dfR / lR);
}
`;
}

/** The composite's own entry point, which the FUSED pass replaces. */
function texturedMainWgsl(): string {
  return `
@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  let p = v.vWorld;
  let px = worldPx();
  let c0 = cellAt(p);
  return terrainMain(p, px, c0, kindOf(c0));
}
`;
}

/**
 * The analytic terrain module of one look: the header, the shared prelude
 * body, the picture and the fragment entry point.
 *
 * It declares the pass's own block: it is the module the roster and the
 * parse check read. `../terrain.ts` `terrainProgram` builds the engine's
 * module through `worldProgram` instead, with the driver's three uniforms
 * in front, because the vertex stage of the world quad reads them out of
 * the same struct.
 */
export function terrainFragWgsl(style: TerrainStyle = TERRAIN_STYLE_DEFAULT): string {
  return wgslModule({ uniforms: terrainAllUniforms(style), body: terrainBodyWgsl(style) });
}

/**
 * The analytic terrain BODY of one look: the shared prelude body, the
 * picture and the fragment entry, with no header and no vertex entry.
 *
 * `../terrain.ts` `terrainProgram` hands it to `worldProgram`, which is the
 * one assembly rule of the engine (`./module.ts`).
 */
export function terrainBodyWgsl(style: TerrainStyle = TERRAIN_STYLE_DEFAULT): string {
  return PRELUDE_BODY_WGSL + commonSrcWgsl(style) + analyticWgsl();
}

/**
 * Everything the composite of one look declares EXCEPT its entry point: the
 * shared picture and `terrainMain`, as functions and module consts alone.
 */
export function terrainTexBodyWgsl(style: TerrainStyle = TERRAIN_STYLE_DEFAULT): string {
  return commonSrcWgsl(style) + texturedWgsl();
}

/** The terrain composite module of one look, header and entry point included. */
export function terrainTexFragWgsl(style: TerrainStyle = TERRAIN_STYLE_DEFAULT): string {
  return wgslModule({
    uniforms: terrainTexAllUniforms(style), body: terrainTexFragBodyWgsl(style),
  });
}

/**
 * The terrain composite BODY of one look, ENTRY POINT included: the shared
 * prelude body, the body above, and `fs`.
 */
export function terrainTexFragBodyWgsl(
  style: TerrainStyle = TERRAIN_STYLE_DEFAULT,
): string {
  return PRELUDE_BODY_WGSL + terrainTexBodyWgsl(style) + texturedMainWgsl();
}
